import React from 'react';
import { useTranslation } from 'react-i18next';
import type { IntegrationLivenessState, IntegrationStatus } from '../../services/wiberryAgentService';
import { formatBytes, formatFreshness } from './wiberryFormat';
import './IntegrationStatusIndicators.css';

/**
 * S9 — the three indicators for a tenant's single wiberry integration:
 * **online/offline** (heartbeat), **last sync time**, and **transferred volume**.
 *
 * The heartbeat is the reason this exists. An agent runs on a box inside the
 * customer's network; it reports success and reports nothing when it dies, and
 * silence is also what a healthy agent on a quiet farm looks like. So the dot
 * renders a judgement the SERVER made against the registry's declared cadence,
 * not a flag the agent set — which is why `state` arrives already decided and
 * this component never compares timestamps itself.
 *
 * Four states, not two. `never_seen` and `revoked` are deliberately not folded
 * into "offline": they send a farmer to three different places (check the box /
 * finish the install / you disconnected this on purpose), and one word for all
 * three is a support call.
 */

const STATE_MODIFIER: Record<IntegrationLivenessState, string> = {
  online: 'online',
  offline: 'offline',
  never_seen: 'pending',
  revoked: 'revoked',
};

interface Props {
  status: IntegrationStatus;
}

const IntegrationStatusIndicators: React.FC<Props> = ({ status }) => {
  const { t } = useTranslation();

  // Not connected is a different screen, not a greyed-out version of this one.
  // It points at `/connect` — the RFC 8628 verification page — because until A3
  // landed that page did not exist, and the agent's printed URI led nowhere.
  if (!status.connected || !status.liveness) {
    return (
      <p className="integration-status__line integration-status__line--ok" data-testid="integration-status-empty">
        {t('integrations.status.notConnected', 'Kein Agent verbunden.')}{' '}
        <a href="/connect">{t('integrations.status.connectLink', 'Agent verbinden')}</a>
      </p>
    );
  }

  const { liveness, last_sync: lastSync } = status;
  const state = liveness.state;

  const stateLabel = {
    online: t('integrations.status.online', 'Online'),
    offline: t('integrations.status.offline', 'Offline'),
    never_seen: t('integrations.status.neverSeen', 'Noch nie verbunden'),
    revoked: t('integrations.status.revoked', 'Getrennt'),
  }[state];

  // The threshold is stated rather than kept a server secret: "erwartet alle
  // 1 h" is what makes "Offline" actionable instead of alarming.
  //
  // The number is composed OUTSIDE `t()` rather than interpolated into it. A
  // missing translation falls back to the default string, and a fallback that
  // never runs interpolation renders the literal `{{hours}}` on a farmer's
  // screen — a defect that only appears in the locale nobody tested.
  const cadenceHours =
    status.cadence_seconds && status.cadence_seconds % 3600 === 0 ? status.cadence_seconds / 3600 : null;
  const cadenceHint = cadenceHours ? `${t('integrations.status.cadenceEvery', 'erwartet alle')} ${cadenceHours} h` : null;

  return (
    <dl className="integration-status" data-testid="integration-status">
      <div className="integration-status__tile">
        <dt className="integration-status__label">{t('integrations.status.connection', 'Verbindung')}</dt>
        <dd className="integration-status__value">
          <span
            className={`integration-status__dot integration-status__dot--${STATE_MODIFIER[state]}`}
            // The dot is decorative — the state is already in the adjacent text,
            // and a screen reader announcing "grüner Punkt" adds nothing.
            aria-hidden="true"
          />
          <span data-testid="integration-status-state">{stateLabel}</span>
          {cadenceHint && <span className="integration-status__hint"> ({cadenceHint})</span>}
        </dd>
      </div>

      <div className="integration-status__tile">
        <dt className="integration-status__label">{t('integrations.status.lastSync', 'Letzte Synchronisierung')}</dt>
        <dd className="integration-status__value" data-testid="integration-status-last-sync">
          {/* `finished_at` when the run is done, `started_at` while it is still
              running — "läuft seit 14:00" is the honest reading of an in-flight
              sync, where a finish time would be a lie and a dash would hide it. */}
          {formatFreshness(lastSync?.finished_at ?? lastSync?.started_at ?? liveness.last_seen_at)}
        </dd>
      </div>

      <div className="integration-status__tile">
        <dt className="integration-status__label">{t('integrations.status.volume', 'Übertragene Daten')}</dt>
        <dd className="integration-status__value" data-testid="integration-status-volume">
          {formatBytes(lastSync?.bytes_synced)}
        </dd>
      </div>
    </dl>
  );
};

/**
 * The last sync's error line — what the AGENT reported, not what our pipeline
 * observed.
 *
 * Three outcomes, and the third is the one that matters. "Keine Fehler" is a
 * positive statement earned from an actual empty result; the error lines are
 * shown verbatim because the agent is the only witness to what happened on the
 * farmer's own box; and when the log read itself failed we say we could not
 * check. Rendering "no errors" off a question nobody managed to ask would be an
 * invented reassurance, which is worse than showing nothing at all.
 */
export const IntegrationSyncErrorLine: React.FC<Props> = ({ status }) => {
  const { t } = useTranslation();

  if (!status.connected) return null;
  // No run yet is not "no errors" — there is nothing to have gone wrong.
  if (!status.last_sync) return null;

  const errors = status.last_sync_errors ?? [];

  if (!status.last_sync_errors_available) {
    return (
      <p className="integration-status__line integration-status__line--unknown" data-testid="integration-sync-errors">
        {t('integrations.status.errorsUnknown', 'Fehlerstatus der letzten Synchronisierung nicht abrufbar.')}
      </p>
    );
  }

  if (errors.length === 0) {
    return (
      <p className="integration-status__line integration-status__line--ok" data-testid="integration-sync-errors">
        {t('integrations.status.noErrors', 'Letzte Synchronisierung: keine Fehler gemeldet.')}
      </p>
    );
  }

  return (
    <div
      className="integration-status__line integration-status__line--error"
      // `alert` and not `status`: a failed sync is the reason the farmer opened
      // this page, and it must be announced rather than waited for.
      role="alert"
      data-testid="integration-sync-errors"
    >
      <p className="integration-status__line-title">
        {t('integrations.status.errorsTitle', 'Letzte Synchronisierung: Fehler gemeldet')}
      </p>
      <ul className="integration-status__errors">
        {errors.map((error) => (
          <li key={`${error.logged_at}-${error.event}`} className="integration-status__error">
            <span className="integration-status__error-time">{formatFreshness(error.logged_at)}</span>{' '}
            {/* The agent's own wording, verbatim — it is the only witness to
                what happened inside the customer's network, and paraphrasing a
                MySQL grant error into "Sync fehlgeschlagen" throws away the one
                sentence that says how to fix it. */}
            <span className="integration-status__error-message">{error.message || error.event}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default IntegrationStatusIndicators;
